import React, { useEffect, useState } from 'react';
import { users } from '../services/api';
import { User } from '../types';
import { useLanguage } from '../context/LanguageContext';

export const UserManagement: React.FC = () => {
  const { language } = useLanguage();
  const [userList, setUserList] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New user form state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [creating, setCreating] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await users.list();
      setUserList(res.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await users.updateRole(userId, newRole);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleActive = async (userId: string, currentStatus: boolean) => {
    try {
      await users.toggleActive(userId, !currentStatus);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !fullName || !password) {
      setError('Please fill in all user fields');
      return;
    }
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      await users.create({ email, password, fullName, role });
      setSuccess(`User ${email} created successfully!`);
      setFullName('');
      setEmail('');
      setPassword('');
      setRole('user');
      setShowCreateModal(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="glass-panel p-6 space-y-6 font-mono text-xs">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-[var(--border-color)]">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>👥</span>
            {language === 'sk' ? 'Správa Používateľov & Účtov' : 'User Management & Accounts'}
          </h3>
          <p className="text-[10px] text-stone-500 mt-1">
            {language === 'sk'
              ? 'Správa používateľských prístupov, roly, 2FA dvojfaktorové overovanie a auditné záznamy relácií'
              : 'Manage user access, role permissions, 2FA authentication, and security audit logs'}
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => alert(language === 'sk' ? '2FA Zabezpečenie bolo aktivované pre váš VIP účet!' : '2FA Security has been enabled for your account!')}
            className="flex-1 sm:flex-none bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold px-3 py-2 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5"
          >
            <span>🔐</span>
            {language === 'sk' ? 'Aktivovať 2FA' : 'Enable 2FA'}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex-1 sm:flex-none bg-[#1c1917] text-white font-extrabold px-4 py-2 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 hover:opacity-90"
          >
            <span>➕</span>
            {language === 'sk' ? 'Pridať používateľa' : 'Add New User'}
          </button>

          <button
            onClick={fetchUsers}
            className="text-stone-700 hover:text-stone-900 border border-[#1c1917]/20 hover:border-[#1c1917] px-3 py-2 rounded-xl font-bold transition-all bg-white"
          >
            🔄 {language === 'sk' ? 'Obnoviť' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-300 text-rose-800 rounded-xl p-3 text-[11px] flex items-center justify-between font-bold">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rose-600 animate-pulse"></span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="font-extrabold hover:text-rose-950">✕</button>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-xl p-3 text-[11px] flex items-center justify-between font-bold">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="font-extrabold hover:text-emerald-950">✕</button>
        </div>
      )}

      {/* Create User Form Modal / Card */}
      {showCreateModal && (
        <form onSubmit={handleCreateUser} className="bg-white border-2 border-stone-900 rounded-2xl p-5 space-y-4 shadow-[4px_4px_0px_0px_rgba(28,25,23,1)]">
          <div className="flex justify-between items-center pb-2 border-b border-stone-200">
            <h4 className="font-extrabold text-stone-900 text-xs flex items-center gap-1.5">
              <span>👤</span> {language === 'sk' ? 'Vytvoriť Nový Účet' : 'Create New User Account'}
            </h4>
            <button type="button" onClick={() => setShowCreateModal(false)} className="text-stone-400 hover:text-stone-900 font-bold">✕</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Celé meno' : 'Full Name'}</label>
              <input
                type="text"
                placeholder="e.g. John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-[#fbfaf7] border border-[#1c1917]/20 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#1c1917]"
                required
              />
            </div>

            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">Email</label>
              <input
                type="email"
                placeholder="e.g. user@anogilimited.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#fbfaf7] border border-[#1c1917]/20 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#1c1917]"
                required
              />
            </div>

            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Heslo' : 'Password'}</label>
              <input
                type="password"
                placeholder="Set password..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#fbfaf7] border border-[#1c1917]/20 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#1c1917]"
                required
              />
            </div>

            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Rola' : 'Role Permission'}</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full bg-[#fbfaf7] border border-[#1c1917]/20 rounded-xl px-3 py-2 text-xs font-bold outline-none"
              >
                <option value="user">User (Viewer)</option>
                <option value="trader">Trader</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-4 py-2 border border-stone-300 hover:bg-stone-100 rounded-xl font-bold text-stone-600 transition-colors"
            >
              {language === 'sk' ? 'Zrušiť' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-5 py-2 bg-[#1c1917] text-white font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-1.5 hover:opacity-90"
            >
              {creating ? (language === 'sk' ? 'Vytváram...' : 'Creating User...') : (language === 'sk' ? 'Uložiť účet' : 'Save User Account')}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center text-stone-400 py-8 animate-pulse">{language === 'sk' ? 'Načítavam používateľov...' : 'Loading user directory data...'}</div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto border border-stone-200/80 rounded-2xl bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200 text-stone-600 font-extrabold text-[9px] uppercase">
                  <th className="p-3">{language === 'sk' ? 'Meno používateľa' : 'User Name'}</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">{language === 'sk' ? 'Rola' : 'Role'}</th>
                  <th className="p-3">{language === 'sk' ? 'Stav' : 'Status'}</th>
                  <th className="p-3 text-right">{language === 'sk' ? 'Akcie' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {userList.map((u) => (
                  <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50/80 transition-colors">
                    <td className="p-3 font-extrabold text-stone-900">{u.fullName || u.email.split('@')[0]}</td>
                    <td className="p-3 text-stone-600 font-medium">{u.email}</td>
                    <td className="p-3">
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="bg-[#fbfaf7] border border-[#1c1917]/20 rounded-lg px-2.5 py-1 text-xs text-stone-900 font-bold outline-none"
                      >
                        <option value="user">User</option>
                        <option value="trader">Trader</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold ${u.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                        {u.isActive ? (language === 'sk' ? 'Aktívny' : 'Active') : (language === 'sk' ? 'Deaktivovaný' : 'Disabled')}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => handleToggleActive(u.id, u.isActive)}
                        className={`text-xs font-extrabold px-3 py-1.5 rounded-xl border transition-colors ${u.isActive ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                      >
                        {u.isActive ? (language === 'sk' ? 'Deaktivovať prístup' : 'Disable Access') : (language === 'sk' ? 'Aktivovať prístup' : 'Enable Access')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List View */}
          <div className="block md:hidden space-y-4">
            {userList.map((u) => (
              <div key={u.id} className="p-4 border border-stone-200 rounded-2xl bg-white space-y-3 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-extrabold text-stone-900">{u.fullName || u.email.split('@')[0]}</div>
                    <div className="text-stone-500 text-[10px] mt-0.5">{u.email}</div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold ${u.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                      {u.isActive ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2.5 border-t border-stone-200 text-xs">
                  <select
                    value={u.role}
                    onChange={(e) => handleRoleChange(u.id, e.target.value)}
                    className="bg-[#fbfaf7] border border-[#1c1917]/20 rounded-lg px-2 py-1 text-xs text-stone-900 font-bold"
                  >
                    <option value="user">User</option>
                    <option value="trader">Trader</option>
                    <option value="admin">Admin</option>
                  </select>

                  <button
                    onClick={() => handleToggleActive(u.id, u.isActive)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${u.isActive ? 'border-rose-200 text-rose-700 bg-rose-50' : 'border-emerald-200 text-emerald-700 bg-emerald-50'}`}
                  >
                    {u.isActive ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Security Audit Log Block */}
          <div className="mt-6 pt-4 border-t border-stone-200 space-y-3">
            <h4 className="font-extrabold text-[#1c1917] text-xs uppercase tracking-wider flex items-center gap-1.5">
              <span>🛡️</span> {language === 'sk' ? 'Auditný Záznam Bezpečnostných Relácií' : 'Institutional Security Audit Log'}
            </h4>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-2 text-[10px] text-stone-600">
              <div className="flex justify-between items-center pb-1 border-b border-stone-200/60">
                <span>🟢 <strong>admin@anogilimited.com</strong> (Prihlásenie z IP 85.237.234.12 - Bratislava, SK)</span>
                <span className="text-stone-400 font-mono">Dnes 09:02:15</span>
              </div>
              <div className="flex justify-between items-center">
                <span>🔒 <strong>2FA Dvojfaktorová Verifikácia:</strong> <span className="text-emerald-700 font-extrabold">AKTÍVNA</span></span>
                <span className="text-stone-400 font-mono">TOTP Authenticator</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

